import { useState, useCallback } from 'react'
import { heapEvent } from '@equipmentshare/heaper'
import {
	useCompleteDemandRequestLineItemsMutation,
	useReopenDemandRequestLineItemsMutation,
	useCancelDemandRequestLineItemsMutation,
} from '../../../graphql'

type DemandRequest = {
	id: string
}

type UseBulkLineItemActionsParams = {
	request: DemandRequest | undefined | null
	selectedLineItemIds: string[]
	addToast: ( message: string ) => void
	clearSelection: () => void
}

/**
 * Hook for managing bulk line item actions (complete, reopen, cancel multiple items)
 */
export function useBulkLineItemActions( {
	request,
	selectedLineItemIds,
	addToast,
	clearSelection,
}: UseBulkLineItemActionsParams ) {
	const [ isBulkActionLoading, setIsBulkActionLoading ] = useState( false )
	const [ completeLineItems ] = useCompleteDemandRequestLineItemsMutation()
	const [ reopenLineItems ] = useReopenDemandRequestLineItemsMutation()
	const [ cancelLineItems ] = useCancelDemandRequestLineItemsMutation()

	const handleBulkCompleteLineItems = useCallback( async() => {
		if ( !request || selectedLineItemIds.length === 0 ) {
			return
		}

		setIsBulkActionLoading( true )

		try {
			const result = await completeLineItems( {
				variables: {
					input: {
						demandRequestId: request.id,
						lineItemIds: selectedLineItemIds,
					},
				},
			} )

			if ( result.data?.completeDemandRequestLineItems?.success ) {
				addToast( `${selectedLineItemIds.length} line item(s) marked as completed` )
				clearSelection()

				heapEvent( 'Inventory - Bulk Actions - Mark Completed', {
					demand_request_id: request.id,
					item_count: selectedLineItemIds.length,
				} )
			} else {
				const errorMessage = result.data?.completeDemandRequestLineItems?.message ?? 'Unknown error occurred'
				addToast( `Failed to complete line items: ${errorMessage}` )
			}
		} catch ( err ) {
			addToast( `Failed to complete line items: ${err instanceof Error ? err.message : 'Unknown error'}` )
		} finally {
			setIsBulkActionLoading( false )
		}
	}, [ request, selectedLineItemIds, completeLineItems, addToast, clearSelection ] )

	const handleBulkReopenLineItems = useCallback( async() => {
		if ( !request || selectedLineItemIds.length === 0 ) {
			return
		}

		setIsBulkActionLoading( true )

		try {
			const result = await reopenLineItems( {
				variables: {
					input: {
						demandRequestId: request.id,
						lineItemIds: selectedLineItemIds,
					},
				},
			} )

			if ( result.data?.reopenDemandRequestLineItems?.success ) {
				addToast( `${selectedLineItemIds.length} line item(s) reopened` )
				clearSelection()

				heapEvent( 'Inventory - Bulk Actions - Reopen', {
					demand_request_id: request.id,
					item_count: selectedLineItemIds.length,
				} )
			} else {
				const errorMessage = result.data?.reopenDemandRequestLineItems?.message ?? 'Unknown error occurred'
				addToast( `Failed to reopen line items: ${errorMessage}` )
			}
		} catch ( err ) {
			addToast( `Failed to reopen line items: ${err instanceof Error ? err.message : 'Unknown error'}` )
		} finally {
			setIsBulkActionLoading( false )
		}
	}, [ request, selectedLineItemIds, reopenLineItems, addToast, clearSelection ] )

	const handleBulkCancelLineItems = useCallback( async() => {
		if ( !request || selectedLineItemIds.length === 0 ) {
			return
		}

		setIsBulkActionLoading( true )

		try {
			const result = await cancelLineItems( {
				variables: {
					input: {
						demandRequestId: request.id,
						lineItemIds: selectedLineItemIds,
					},
				},
			} )

			if ( result.data?.cancelDemandRequestLineItems?.success ) {
				addToast( `${selectedLineItemIds.length} line item(s) cancelled` )
				clearSelection()

				heapEvent( 'Inventory - Bulk Actions - Cancel', {
					demand_request_id: request.id,
					item_count: selectedLineItemIds.length,
				} )
			} else {
				const errorMessage = result.data?.cancelDemandRequestLineItems?.message ?? 'Unknown error occurred'
				addToast( `Failed to cancel line items: ${errorMessage}` )
			}
		} catch ( err ) {
			addToast( `Failed to cancel line items: ${err instanceof Error ? err.message : 'Unknown error'}` )
		} finally {
			setIsBulkActionLoading( false )
		}
	}, [ request, selectedLineItemIds, cancelLineItems, addToast, clearSelection ] )

	return {
		isBulkActionLoading,
		handleBulkCompleteLineItems,
		handleBulkReopenLineItems,
		handleBulkCancelLineItems,
	}
}
